import {defineComponent, ref, watch, computed} from "vue";
import {ResponderAuditLog, ResponderResponseAuditLog} from '@/api/klip-api.proxy';
import DateUtil from '@/app/shared/helpers/date-util';
import {AdminMapRequestDetailAsUna} from "@/app/admin/admin-routes";

export default defineComponent({
    name: 'KlMaprequestRespondersAuditLog',
    props: {
        auditLogs: {
            type: Array<ResponderAuditLog>,
            default: [],
            required: true
        },
        mapRequestId: {
            type: String,
            required: true
        },
        modClickableRows: {
            type: Boolean,
            required: false,
            default: false
        },
        modToolTip: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: ['row-clicked'],
    setup(props, { emit }) {
        const responderAuditLog = ref<ResponderAuditLog[]>([]);
        const toggleRemovedKlimClicked = ref<boolean>(false);
        const responderAuditLogsTableLoading = ref<boolean>(true);

        watch(() => props.auditLogs, () => {
            responderAuditLog.value = props.auditLogs.filter(r => !r.removed);
            responderAuditLogsTableLoading.value = false;
        }, {immediate: true, deep: true});

        const parseUtilityNetworkAuthorityResponse = (responderAuditLog: ResponderResponseAuditLog) => {
            if (!responderAuditLog) {
                return '';
            }
            let userIcon = '';
            if (responderAuditLog.respondedByKlipContactId) {
                const icon = responderAuditLog.isServiceAccount ? 'server' : 'user';
                userIcon = responderAuditLog.respondedBy && responderAuditLog.respondedBy !== '' && props.modToolTip ?
                    `<vl-icon icon="${icon}" v-vl-tooltip.left="'${responderAuditLog.respondedBy}'" />` :
                    `<vl-icon icon="${icon}" />`;
            }

            const timeStamp = responderAuditLog.respondedAt ? DateUtil.formatDateTime(responderAuditLog.respondedAt) : ' ';
            const reminderIcon = responderAuditLog.reminderSent ? `<vl-icon icon="alert-triangle-filled" v-vl-tooltip.right="'${responderAuditLog.reminderSent}'" /> ` : '';
            return `<div>${userIcon} ${timeStamp} ${reminderIcon}</div>`;
        }

        const cellParser = (row: IRow, column: IColumn) => {
            const rowClass = row.removed ? 'obsolete' : 'active';
            const routeParams = {
                id: props.mapRequestId,
                zoneId: row.utilityNetworkAuthorityZoneId,
                archived: 'false'
            };
            let rowTemplate: string = `<div class='${rowClass}'>${row[column.key]}</div>`;
            switch (column.key) {
                case 'utilityNetworkAuthorityAndZoneName': {
                    rowTemplate = `<div class='${rowClass}'>${row.isKlim ? `<img src="/img/logo-klim.svg" class="klim-pictogram" alt="KLIM pictogram" />` : ''}${row.utilityNetworkAuthorityDisplayName} - ${row.utilityNetworkAuthorityZoneName}</div>`;
                    break;
                }
                case 'utilityNetworkAuthorityForMapRequestAdded': {
                    rowTemplate = `<div class='${rowClass}'>${!row.removed ? parseUtilityNetworkAuthorityResponse(row.addedResponse) : ''} </div>`;
                    break;
                }
                case 'utilityNetworkAuthorityForMapRequestConfirmed': {
                    rowTemplate = `<div class='${rowClass}'>${!row.removed ? parseUtilityNetworkAuthorityResponse(row.confirmedResponse) : ''} </div>`;
                    break;
                }
                case 'utilityNetworkAuthorityForMapRequestResponded': {
                    rowTemplate = `<div class='${rowClass}'>${!row.removed ? parseUtilityNetworkAuthorityResponse(row.respondedResponse) : ''} </div>`;
                    break;
                }
            }

            return {
                template: row.removed || !props.modClickableRows ? rowTemplate : `<kl-router-link :to='${JSON.stringify(AdminMapRequestDetailAsUna)}' :params='${JSON.stringify(routeParams)}'>${rowTemplate}</kl-router-link>`,
            };
        };

        const meta = computed((): any => {
            return {
                totalRows: responderAuditLog.value.length,
                resultsPerPage: responderAuditLog.value.length,
                currentPage: 0,
            };
        });

        const columns = ref<IColumn[]>([
            {
                key: 'utilityNetworkAuthorityAndZoneName',
                label: 'KLB en zone',
                sortable: false,
                isVisible: true,
                width: 35,
                parser: cellParser,
            },
            {
                key: 'utilityNetworkAuthorityForMapRequestAdded',
                label: 'Toegevoegd',
                sortable: false,
                isVisible: true,
                width: 18,
                parser: cellParser,
            },
            {
                key: 'utilityNetworkAuthorityForMapRequestConfirmed',
                label: 'Ontvangst bevestigd',
                sortable: false,
                isVisible: true,
                width: 18,
                parser: cellParser,
            },
            {
                key: 'utilityNetworkAuthorityForMapRequestResponded',
                label: 'Beantwoord',
                sortable: false,
                isVisible: true,
                width: 18,
                parser: cellParser,
            },
            {
                key: 'imklVersion',
                label: 'Antwoordtype',
                sortable: false,
                isVisible: true,
                width: 11,
                parser: cellParser,
            },
        ]);

        const hasResponders = computed((): boolean => {
            return responderAuditLog.value.length > 0;
        });

        const rowClicked = (row: IRow) => {
            if (row && row.id && !row.removed && props.modClickableRows) {
                emit('row-clicked', row.utilityNetworkAuthorityZoneId);
            }
        };

        const toggleRemovedKlim = () => {
            responderAuditLog.value = props.auditLogs;
            toggleRemovedKlimClicked.value = true;
        };

        return {
            responderAuditLogsTableLoading,
            meta,
            columns,
            rowClicked,
            responderAuditLog,
            hasResponders,
            toggleRemovedKlimClicked,
            toggleRemovedKlim
        }
    }
});
